import { Autocomplete, TextField } from "@mui/material";

/*
  Contains a variety of helper functions for the DataGrid component.
*/

// Storage keys for persisting user preferences on visible columns and their order
// Use these keys in localStorage
export const visibleColumnsStorageKey = "visibleColumns";
export const visibleColumnOrderStorageKey = "visibleColumnOrder";

/**
 * Creates a text column configuration for the data grid.
 * @param {*} id The ID of the column.
 * @param {*} header The header text for the column.
 * @param {*} extra Additional properties for the column.
 * @returns The column configuration object.
 */
export function createTextColumn(id, header, extra = {}) {
  return {
    id,
    accessorKey: id,
    header,
    fieldType: "text",
    parseValue: (rawValue, row) => parseTextColumnValue(rawValue, row, id),
    ...extra,
  };
}

/**
 * Parses the value of a text column in the data grid.
 * @param {*} rawValue The raw value from the input.
 * @param {*} row The row data.
 * @param {*} columnId The ID of the column.
 * @returns The parsed value.
 */
function parseTextColumnValue(rawValue, row, columnId) {
  const currentValue = row?.[columnId];

  if (rawValue === "" && (currentValue == null || currentValue === "")) {
    return currentValue;
  }

  if (typeof currentValue === "number" && rawValue !== "") {
    const parsed = Number(rawValue);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return rawValue;
}

/**
 * Creates a boolean column configuration for the data grid.
 * @param {*} id The ID of the column.
 * @param {*} header The header text for the column.
 * @param {*} extra Additional properties for the column.
 * @returns The column configuration object.
 */
export function createBoolColumn(id, header, extra = {}) {
  return {
    id,
    accessorKey: id,
    header,
    fieldType: "bool",
    ...extra,
  };
}

/**
 * Searchable dropdown editor for columns with a fixed set of string options.
 * @param {*} value The current value.
 * @param {*} onCommit Callback when a value is committed.
 * @param {*} onCancel Optional callback when the edit is abandoned (Escape).
 * @param {*} options Array of options for the select.
 * @param {*} placeholder Optional placeholder text.
 * @returns The Autocomplete component.
 */
export function TextSelectEditor({
  value,
  onCommit,
  onCancel,
  options = [],
  placeholder = "Select",
}) {
  // A cell can hold a value that is not a valid option — most often the read-only explanation shown
  // for a column that does not apply to this row. Coerce it to null so MUI does not log an
  // out-of-range warning; committing is rejected downstream regardless.
  const selectedValue = options.includes(value) ? value : null;

  return (
    <Autocomplete
      size='small'
      fullWidth
      autoHighlight
      openOnFocus
      options={options}
      value={selectedValue}
      onChange={(_, nextValue, reason) => {
        // Clearing the text is how a user starts searching, not a commit of an empty value —
        // committing here would close the editor before they can pick anything.
        if (reason === "clear") {
          return;
        }
        onCommit(nextValue ?? "");
      }}
      onBlur={() => onCommit(selectedValue ?? "")}
      renderInput={(params) => (
        <TextField
          {...params}
          variant='standard'
          autoFocus
          placeholder={placeholder}
          onKeyDown={(event) => {
            if (event.key === "Escape" && onCancel) {
              onCancel();
            }
          }}
        />
      )}
      sx={{ minWidth: 140 }}
    />
  );
}
